import React, {useEffect, createContext} from "react";

import Backdrop from '@material-ui/core/Backdrop';
import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles } from '@material-ui/core/styles';

//import api from '../utils/api'
import {useApiHttpState} from './ApiContext'
import { isUnauthorized, SESSION_CHECK_REQUESTED } from '../utils/sessionStatus'

const UserStateContext = createContext();
const UserDispatchContext = createContext();

function userReducer(state, action) {
  switch (action.type) {
    case "LOGIN_SUCCESS":
      return { ...state, isAuthenticated: true, user: action.payload, isCheckingSession: false, sessionExpired: false };
    case "SIGN_OUT_SUCCESS":
      return { ...state, isAuthenticated: false, user: null, isCheckingSession: false, sessionExpired: !!action.sessionExpired };
    case "SESSION_CHECK_FINISHED":
      return state.isCheckingSession ? { ...state, isCheckingSession: false } : state;
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff',
  },
}));


function UserProvider({ children }) {
  const classes = useStyles();
  //const [loading,setLoading] = React.useState(false)
  const {loading,api} = useApiHttpState()
  
  const [state, dispatch] = React.useReducer(userReducer, {
    isAuthenticated: false, user: null, isCheckingSession: true, sessionExpired: false
  });
   
  useEffect(() => {
    let cancelled = false
    let inFlight = false
    const loadUser = async () => {
      if (inFlight) return
      inFlight = true
      try {
        const [error, response] = await api.get('/users/current')
        if (cancelled) return
        if (!error) {
          if (!state.isAuthenticated) dispatch({ type: 'LOGIN_SUCCESS', payload: response.data })
        } else if (isUnauthorized(error)) {
          dispatch({ type: 'SIGN_OUT_SUCCESS', sessionExpired: state.isAuthenticated })
        }
        // Network errors, permission denials, and 5xx do not establish expiry.
      } catch (error) {
        // Retain authentication if the request itself fails.
      } finally {
        inFlight = false
        if (!cancelled) dispatch({ type: 'SESSION_CHECK_FINISHED' })
      }
    }
    const recheck = () => {
      if (state.isAuthenticated) loadUser()
    }
    window.addEventListener(SESSION_CHECK_REQUESTED, recheck)
    window.addEventListener('focus', recheck)
    if (state.isAuthenticated || state.isCheckingSession) loadUser()
    const interval = setInterval(recheck, 60000)
    return () => {
      cancelled = true
      clearInterval(interval)
      window.removeEventListener(SESSION_CHECK_REQUESTED, recheck)
      window.removeEventListener('focus', recheck)
    }
  }, [api, state.isAuthenticated, state.isCheckingSession])

  return (
    <UserStateContext.Provider value={state}>
      <UserDispatchContext.Provider value={dispatch}>
        {(loading || state.isCheckingSession) &&
          <Backdrop className={classes.backdrop} open >
            <CircularProgress color="inherit" />
          </Backdrop>
        }
        {children}
      </UserDispatchContext.Provider>
    </UserStateContext.Provider>
  );
}

function useUserState() {
  const context = React.useContext(UserStateContext);
  if (context === undefined) {
    throw new Error("useUserState must be used within a UserProvider");
  }
  return context;
}

function useUserDispatch() {
  const context = React.useContext(UserDispatchContext);
  if (context === undefined) {
    throw new Error("useUserDispatch must be used within a UserProvider");
  }
  return context;
}

export { UserProvider, useUserState, useLoginUser, useSignOutUser, useUserDispatch, loginUser, signOut };

// ###########################################################

const useLoginUser = () => {
  const dispatch = useUserDispatch()
  dispatch({ type: 'LOGIN_SUCCESS' })  
}
const useSignOutUser = () => {
  const dispatch = useUserDispatch()  
  dispatch({ type: "SIGN_OUT_SUCCESS" });  
}

function loginUser(dispatch, login, password, history, setIsLoading, setError) {
  setError(false);
  setIsLoading(true);

  if (!!login && !!password) {
    setTimeout(() => {
      localStorage.setItem('id_token', 1)
      setError(null)
      setIsLoading(false)
      dispatch({ type: 'LOGIN_SUCCESS' })

      history.push('/app/dashboard')
    }, 2000);
  } else {
    dispatch({ type: "LOGIN_FAILURE" });
    setError(true);
    setIsLoading(false);
  }
}

function signOut(dispatch, history) {
  localStorage.removeItem("id_token");
  dispatch({ type: "SIGN_OUT_SUCCESS" });
  history.push("/login");
}
